// ISDP CUSTOM: shared single-select dropdown rendered with an ODX look — a real
// popover list, NOT the browser-native <select> (which shows the OS dropdown).
// Styles live in the app's global odx-overrides.css (.devhub-odx-select*), so this
// component imports no CSS and stays safe to bundle anywhere (no odx-icon dep either
// — the chevron is drawn in CSS).
//
// The open list is rendered in a portal on <body> with FIXED positioning anchored
// to the trigger, so it escapes any `overflow:hidden`/scroll-container ancestor
// (e.g. Backstage's `main.odx-app-main`) that would otherwise clip it — the bug
// where the last rows of a long table showed a cut-off, "scrollable" dropdown. It
// flips above the trigger when there isn't room below, and closes on scroll/resize
// so it never drifts away from its anchor.
import {
  KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

export type OdxSelectOption = { value: string; label: string; disabled?: boolean };

export type OdxSelectProps = {
  value: string;
  onChange: (value: string) => void;
  options: Array<OdxSelectOption | string>;
  placeholder?: string;
  disabled?: boolean;
  ariaLabel?: string;
  id?: string;
  className?: string;
};

type MenuPos = { left: number; width: number; top?: number; bottom?: number };

// Max popover height must match .devhub-odx-select__list max-height in the CSS so
// the flip-up room calculation lines up with what actually renders.
const MENU_MAX_HEIGHT = 280;
const GAP = 4;

export function OdxSelect({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  disabled = false,
  ariaLabel,
  id,
  className,
}: OdxSelectProps) {
  const norm = useMemo<OdxSelectOption[]>(
    () => options.map(o => (typeof o === 'string' ? { value: o, label: o } : o)),
    [options],
  );
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [menuPos, setMenuPos] = useState<MenuPos | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const selected = norm.find(o => o.value === value);

  // Position the fixed-position menu against the trigger, flipping above when there
  // isn't enough room below in the viewport.
  const reposition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const roomBelow = window.innerHeight - r.bottom;
    const desired = Math.min(MENU_MAX_HEIGHT, norm.length * 40 + 8);
    const flipUp = roomBelow < desired + GAP && r.top > roomBelow;
    setMenuPos(
      flipUp
        ? { left: r.left, width: r.width, bottom: window.innerHeight - r.top + GAP }
        : { left: r.left, width: r.width, top: r.bottom + GAP },
    );
  }, [norm.length]);

  // Compute position synchronously before paint when opening.
  useLayoutEffect(() => {
    if (open) reposition();
  }, [open, reposition]);

  // Close on outside click (trigger + portaled list are both "inside"), and close
  // on scroll/resize so the fixed menu never separates from its anchor.
  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t) || listRef.current?.contains(t)) return;
      setOpen(false);
    };
    // Close on an ancestor scroll (so the fixed menu never drifts from its
    // anchor), but NOT when the scroll originates inside the option list itself —
    // that internal scroll is exactly what we want to allow. capture:true is what
    // lets us see inner-container scrolls at all, so we must explicitly exclude the
    // list's own scroll or the very first wheel tick would close the menu (and the
    // list could never scroll — the wheel would fall through to the page).
    const onScrollOrResize = (e?: Event) => {
      if (e?.type === 'scroll') {
        const t = e.target as Node | null;
        if (t && listRef.current && (listRef.current === t || listRef.current.contains(t))) {
          return;
        }
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    // capture:true catches scrolls on inner scroll containers, not just window.
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  // When opening, highlight the current selection.
  useEffect(() => {
    if (open) setActive(norm.findIndex(o => o.value === value));
  }, [open, norm, value]);

  const commit = (i: number) => {
    const opt = norm[i];
    if (opt && !opt.disabled) {
      onChange(opt.value);
      setOpen(false);
    }
  };

  const onKeyDown = (e: ReactKeyboardEvent) => {
    if (disabled) return;
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(a => Math.min(norm.length - 1, a + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(a => Math.max(0, a - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commit(active);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div
      className={`devhub-odx-select${open ? ' is-open' : ''}${disabled ? ' is-disabled' : ''}${className ? ` ${className}` : ''}`}
      ref={rootRef}
    >
      <button
        type="button"
        id={id}
        ref={triggerRef}
        className="devhub-odx-select__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
        onKeyDown={onKeyDown}
      >
        <span className={`devhub-odx-select__value${selected ? '' : ' is-placeholder'}`}>
          {selected ? selected.label : placeholder}
        </span>
        <span className="devhub-odx-select__chevron" aria-hidden="true" />
      </button>
      {open &&
        menuPos &&
        createPortal(
          <ul
            ref={listRef}
            className="devhub-odx-select__list devhub-odx-select__list--fixed"
            role="listbox"
            tabIndex={-1}
            style={{
              left: menuPos.left,
              // minWidth, not width: the popover lines up with the trigger but may grow
              // past it when an option is longer. Pinning it to the trigger's exact width
              // truncated the option text itself (the long category names, and the same in
              // the admin curation editor) — the list is portalled to <body>, so growing
              // is free and clips nothing.
              minWidth: menuPos.width,
              maxWidth: `calc(100vw - ${menuPos.left}px - 16px)`,
              ...(menuPos.top !== undefined ? { top: menuPos.top } : {}),
              ...(menuPos.bottom !== undefined ? { bottom: menuPos.bottom } : {}),
            }}
          >
            {norm.map((o, i) => (
              <li
                key={o.value}
                role="option"
                aria-selected={o.value === value}
                className={`devhub-odx-select__option${o.value === value ? ' is-selected' : ''}${i === active ? ' is-active' : ''}${o.disabled ? ' is-disabled' : ''}`}
                onMouseEnter={() => setActive(i)}
                onMouseDown={e => {
                  // prevent the trigger's outside-click handler from firing first
                  e.preventDefault();
                  commit(i);
                }}
              >
                {o.label}
              </li>
            ))}
          </ul>,
          document.body,
        )}
    </div>
  );
}
